import React, { useMemo } from 'react';
import styled from 'styled-components';
import { AgentProfilePictureByLogin } from 'components/AgentProfilePictureByLogin';
import { statusColors } from 'utils/variables/statusColors';

// Container principal para o card do agente
const AgentCardContainer = styled.div`
  padding: 0.5rem;
  display: flex;
  align-items: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: ${({ borderRadius }) => `calc(${borderRadius}/2)`};
  color: white;
  height: min-content;
`;

// Container para informações do agente
const AgentInfoContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
  flex: 1;
  overflow: hidden;
`;

// Detalhes do agente
const AgentDetails = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.25rem;
`;

// Nome do agente
const AgentName = styled.h3`
  margin-block: 0;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1rem;
  max-width: 200px;
  font-size: 0.9rem;
`;

// Status de pausa do agente
const AgentPauseName = styled.h4`
  color: inherit;
  margin-block: 0;
  line-height: 1rem;
  font-weight: 400;
  font-size: 0.8rem;
  opacity: 0.8;
`;

/**
 * MonitoringAgentQueueCard - Versão simplificada do card de agente para visualização por fila
 *
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.agent - Objeto contendo dados do agente (AgentType)
 * @param {string} props.agent.name - Nome do agente
 * @param {string} props.agent.login - Login do agente
 * @param {string} props.agent.status - Status do agente ('isAvailable'|'isPaused'|'isActivePause')
 * @param {number|null} props.agent.pauseId - ID da pausa atual (se pausado)
 * @param {string} props.borderRadius - Raio da borda da configuração do sistema
 * @param {Array} props.pauses - Lista de pausas disponíveis
 * @returns {JSX.Element} Componente renderizado
 */
export const MonitoringAgentQueueCard = React.memo(
  ({ agent, borderRadius, pauses }) => {
    const { name, login, status, pauseId } = agent;

    // Determinar a cor de fundo baseado no status
    const backgroundColor = useMemo(() => {
      if (status === 'isAvailable') return statusColors.AVAILABLE;
      if (status === 'isPaused' || status === 'isActivePause')
        return statusColors.PAUSE;
      return statusColors.OFFLINE;
    }, [status]);

    // Obter o nome da pausa a partir do ID da pausa
    const pauseName = useMemo(() => {
      if (status === 'isAvailable') return 'Disponível';
      if (!pauseId) return 'Em pausa';

      const pause = pauses.find((p) => p.id === pauseId);
      return pause?.name || 'Em pausa';
    }, [status, pauseId, pauses]);

    return (
      <AgentCardContainer
        backgroundColor={backgroundColor}
        borderRadius={borderRadius}
      >
        <AgentInfoContainer>
          <AgentProfilePictureByLogin
            login={login}
            alt={`Foto de ${name}`}
            style={{
              aspectRatio: '1',
              height: '2rem',
              width: '2rem',
              borderRadius: '100%',
              boxShadow: '0 0 0 1px #00000010'
            }}
          />
          <AgentDetails>
            <AgentName title={name}>{name}</AgentName>
            <AgentPauseName>{pauseName}</AgentPauseName>
          </AgentDetails>
        </AgentInfoContainer>
      </AgentCardContainer>
    );
  }
);

MonitoringAgentQueueCard.displayName = 'MonitoringAgentQueueCard';
